import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    CircularProgress,
    InputAdornment,
    IconButton,
    Card,
    CardContent,
    Stack,
    Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import rutaService, { Ruta } from '../services/rutaService';

interface RutaSearchProps {
    open: boolean;
    onClose: () => void;
    onSelect: (ruta: Ruta) => void;
}

const RutaSearch: React.FC<RutaSearchProps> = ({ open, onClose, onSelect }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchTerm, setSearchTerm] = useState('');
    const [rutas, setRutas] = useState<Ruta[]>([]);
    const [rutasFiltradas, setRutasFiltradas] = useState<Ruta[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadRutas = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await rutaService.getRutas();
            setRutas(data);
            setRutasFiltradas(data);
            if (data.length === 0) {
                setError('No se encontraron rutas.');
            }
        } catch (err: any) {
            setError(`Error al cargar rutas: ${err.message || 'Error desconocido'}`);
            setRutas([]);
            setRutasFiltradas([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleSearch = useCallback(() => {
        const trimmed = searchTerm.trim();
        if (!trimmed) {
            setRutasFiltradas(rutas);
            setError(null);
            return;
        }

        const trimmedLower = trimmed.toLowerCase();
        // Buscar por código de ruta o nombre
        const filtered = rutas.filter(r => {
            const codigoRuta = String(r.codigoRuta || '').includes(trimmed);
            const nombreRuta = r.nombreRuta?.toLowerCase().includes(trimmedLower);
            return codigoRuta || nombreRuta;
        });

        setRutasFiltradas(filtered);
        if (filtered.length === 0) {
            setError('No se encontraron rutas.');
        } else {
            setError(null);
        }
    }, [searchTerm, rutas]);

    const handleSelect = (ruta: Ruta) => {
        onSelect(ruta);
        onClose();
    };

    const handleClose = () => {
        setSearchTerm('');
        setRutasFiltradas(rutas);
        setError(null);
        onClose();
    };

    // Cargar rutas cuando se abre el diálogo
    useEffect(() => {
        if (open) {
            loadRutas();
        } else {
            setSearchTerm('');
            setRutas([]);
            setRutasFiltradas([]);
            setError(null);
        }
    }, [open, loadRutas]);

    // Filtrar cuando cambia el término de búsqueda
    useEffect(() => {
        if (open && rutas.length > 0) {
            handleSearch();
        }
    }, [open, searchTerm, rutas, handleSearch]);

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isSmallScreen}
            sx={{
                '& .MuiDialog-paper': {
                    margin: { xs: 0, sm: 2 },
                    maxHeight: { xs: '100%', sm: '90vh' },
                    height: { xs: '100%', sm: 'auto' }
                }
            }}
        >
            <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                        Buscar Ruta (o Zona)
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            
            <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Código o nombre de ruta"
                        inputProps={{ 'aria-label': 'Buscar ruta' }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch();
                            }
                        }}
                        size={isSmallScreen ? 'small' : 'medium'}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleSearch} disabled={loading} edge="end">
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        sx={{ mb: 1.5 }}
                    />
                </Box>

                {error && (
                    <Typography color="error" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {error}
                    </Typography>
                )}

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && rutasFiltradas.length > 0 && (
                    <>
                        {isSmallScreen ? (
                            // Vista de tarjetas para móviles
                            <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
                                <Stack spacing={2}>
                                    {rutasFiltradas.map((ruta, index) => {
                                        const key = ruta.codigoRuta || index;
                                        return (
                                            <Card 
                                                key={key} 
                                                sx={{ 
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        boxShadow: 3
                                                    }
                                                }}
                                                onClick={() => handleSelect(ruta)}
                                            >
                                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                        <Typography variant="subtitle2" color="primary" fontWeight="bold">
                                                            Código: {ruta.codigoRuta || '-'}
                                                        </Typography>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleSelect(ruta);
                                                            }}
                                                            sx={{ minWidth: 'auto', px: 2 }}
                                                        >
                                                            Seleccionar
                                                        </Button>
                                                    </Box>
                                                    <Divider sx={{ my: 1.5 }} />
                                                    <Stack spacing={1}>
                                                        {ruta.nombreRuta && (
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary" display="block">
                                                                    Nombre de Ruta
                                                                </Typography>
                                                                <Typography variant="body2" fontWeight="medium">
                                                                    {ruta.nombreRuta.trim()}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Stack>
                                                </CardContent>
                                            </Card>
                                        );
                                    })}
                                </Stack>
                            </Box>
                        ) : (
                            // Vista de tabla para pantallas grandes
                            <TableContainer 
                                component={Paper} 
                                sx={{ 
                                    maxHeight: 400,
                                    '& .MuiTableCell-root': {
                                        py: 1.5,
                                        fontSize: '0.875rem'
                                    }
                                }}
                            >
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Código</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 300 }}>Nombre de Ruta</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', width: 120 }} align="center">Acción</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rutasFiltradas.map((ruta, index) => {
                                            const key = ruta.codigoRuta || index;
                                            return (
                                                <TableRow 
                                                    key={key} 
                                                    hover
                                                    sx={{ cursor: 'pointer' }}
                                                    onClick={() => handleSelect(ruta)}
                                                >
                                                    <TableCell>{ruta.codigoRuta || '-'}</TableCell>
                                                    <TableCell>{ruta.nombreRuta?.trim() || '-'}</TableCell>
                                                    <TableCell align="center">
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleSelect(ruta);
                                                            }}
                                                        >
                                                            Seleccionar
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </>
                )}

                {!loading && rutasFiltradas.length === 0 && !error && (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
                        No se encontraron rutas
                    </Typography>
                )}
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                <Button 
                    onClick={handleClose} 
                    fullWidth={isSmallScreen}
                    size={isSmallScreen ? 'medium' : 'small'}
                    variant="outlined"
                >
                    CANCELAR
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RutaSearch;

